/**
 * Keeping track of recording state
 *
 * The recorded channels are kept as a delta of the RECORDING_START / RECORDING_END events, which is
 * only correct while every event arrives. RECORDING_END goes missing in two ways: the PBX skips it
 * when it discards a recording instead of storing it, and a client that is asleep, locked or
 * disconnected never sees it. Both leave a dead channel in the list and the REC badge lit for the
 * rest of the session, so anything that hints the list may be stale re-checks it against
 * /pbx/recording/status, which asks the PBX servers directly and is the authority.
 */

import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useEmployeesStore } from '@/stores/Employees'
import { useApiStore } from '@/stores/Api'

import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'

import type { ValidateTokenExtendedResponse } from '@/TcxApi/Authentication'
import type { PbxRecordingStatusResponse } from '@/TcxApi/PbxRecording'
import { sleep } from '@/utilities/Tools'
import { addRecordingChannel, orphanedRecordingIds, recordingIdsFromStatus, removeRecordingChannel } from '@/utilities/RecordingReconcile'

/** How long to let a late RECORDING_END arrive before verifying a recording whose call has ended */
const ORPHAN_GRACE_MS = 5000

let initCompleted = false

export const useRecordingStateStore = defineStore('recordingState', () => {
	logger.debug('RecordingState: Initializing store')

	const realtime = useRealtimeStore()
	const ss = useSettingsStore()
	const api = useApiStore()
	const employees = useEmployeesStore()

	/** List of channel id for the channels that are being recorded */
	const recordingIds = ref<string[]>([])

	/** True when user is recording */
	const isRecording = computed(() => { return recordingIds.value.length > 0 })

	/** Pending verification of channels whose call appears to be over */
	let orphanTimer: ReturnType<typeof setTimeout> | undefined

	if (realtime.connected) { init() }

	realtime.realtimeEvents.on('Connected', () => {
		init()
	})

	// The machine was asleep or locked: every event published while it was gone is lost, and the
	// socket may well have come back without us noticing, so re-check rather than trust the list.
	globalEvents.on('Wakeup', () => {
		if (isRecording.value) { resync('after wakeup') }
	})

	// A hidden browser tab never reports a wakeup - the detector cannot tell a sleeping machine from
	// a throttled tab, so it starts counting again when the tab comes back. That leaves the tab the
	// user locked their machine on with no wakeup at all, so verify when it is looked at again.
	if (typeof document !== 'undefined') {
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState !== 'visible') { return }
			if (isRecording.value) { resync('window became visible') }
		})
	}

	/** Channels of the calls I currently have ongoing */
	const activeChannels = computed(() => { return employees.myCalls.map(call => call.channel) })

	// A recording cannot outlive its call. When a recorded channel is no longer among my ongoing
	// calls we have missed its RECORDING_END - give a late one a moment to arrive, then verify.
	watch(activeChannels, channels => {
		if (orphanTimer) { return }
		if (orphanedRecordingIds(recordingIds.value, channels).length === 0) { return }

		orphanTimer = setTimeout(() => {
			orphanTimer = undefined
			const orphans = orphanedRecordingIds(recordingIds.value, activeChannels.value)

			if (orphans.length === 0) { return }
			logger.warning(`RecordingState: No ongoing call for recorded channel(s) ${orphans.join(', ')} - verifying recording state`)
			resync('orphaned recording')
		}, ORPHAN_GRACE_MS)
	})

	async function init() {
		await sleep(1)
		await ss.ready()

		if (!initCompleted) {
			initCompleted = true
			realtime.pbxEvents.on('RECORDING_START', event => {
				if (event.employee === ss.auth?.employee) {
					recordingIds.value = addRecordingChannel(recordingIds.value, event.channel)
					logger.info(`RecordingState: Employee is recording the call with id: ${event.channel}`)
				}
			})

			realtime.pbxEvents.on('RECORDING_END', event => {
				if (event.employee === ss.auth?.employee) {
					recordingIds.value = removeRecordingChannel(recordingIds.value, event.channel)
					logger.info('RecordingState: Employee stopped recording the call with id: ' + event.channel)
				}
			})

			realtime.configEvents.on('EMPLOYEE_UPDATED', async (event) => {
				if (ss.auth && event.id === ss.auth.employee) {
					try {
						const res = await api.get<ValidateTokenExtendedResponse>(`/auth/validateToken?extend=true&token=${ss.token}`)

						ss.auth.recordingMode = res.data.recordingMode
					}
					catch (_) { /** Ignore */ }
				}
			})
		}

		await resync('startup')
	}

	/**
	 * Rebuild the recorded channels from the authoritative state on the PBX servers
	 * @param reason Why we are re-checking, for the log
	 */
	async function resync(reason: string) {
		try {
			const res = await api.get<PbxRecordingStatusResponse>('/pbx/recording/status')

			recordingIds.value = recordingIdsFromStatus(res.data)

			logger.info(`RecordingState: Checked recording state (${reason}) - currently we are ` + (isRecording.value ? 'recording ' + recordingIds.value.length + ' calls' : 'not recording'))
		}
		catch (err) {
			// Leaving a stale list in place is what keeps the REC badge lit, so say so rather than
			// swallow it - the next trigger will try again.
			logger.warning(`RecordingState: Could not check recording state (${reason}) - ${(err as Error).message}`)
		}
	}

	return { isRecording, recordingIds, resync }
})
